import {Router} from '@angular/router';

import {EventEmitter} from '@angular/core';
import {Observable, Subject, Subscription} from 'rxjs';

import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';

import {ApiError, ApiResponse, HttpStatusCodesEnum} from '@spa/api';
import {FilterModel} from '@spa/api/lib/common';

import {Pagination, ReportTableData, ReportTableDataRow} from 'appShared/components';
import {EventsService} from 'appEntries/panel/services';
import {CacheCollectionService} from 'appShared/services';
import {BaseReportComponent} from './base-report-component';
import {SortColumnInterface} from 'appEntries/panel/interfaces';

export abstract class ReportComponent<ReportInterfaceType, ReportModelType>
    extends BaseReportComponent<ReportInterfaceType, ReportModelType> {

    public sortedColumn: { selector: string, ascending: boolean };

    public filterModelSubject: Subject<FilterModel>;
    public filterModel: FilterModel;

    protected constructor(
        router: Router,
        eventsService: EventsService,
        cacheCollectionService: CacheCollectionService
    ) {
        super(eventsService, router, cacheCollectionService);

        this.sortedColumn = { selector: null, ascending: false };
        this.modelsCollection = [];
    }

    abstract getInitialFilterModel(itemsPerPage?: number): FilterModel;

    abstract getFilterModel(data: { [p: string]: number | string | boolean }): FilterModel;

    abstract getReportHandler(filter: FilterModel): Observable<ApiResponse<ApiError[] | ReportInterfaceType[]>>;

    onInit(): void {
        super.onInit();
        // tslint:disable-next-line:max-line-length
        const sortedColumnCache = this.cacheCollectionService.getItem(this.componentStorageKeyProvider.getFilterModelKey())?.data as FilterModel;
        // tslint:disable-next-line:max-line-length
        this.sortedColumn = sortedColumnCache ? {selector: sortedColumnCache.order_column, ascending: sortedColumnCache.order_ascending} : { selector: null, ascending: false };
        const paginationCache = this.cacheCollectionService.getItem(this.componentStorageKeyProvider.getPaginationConfigKey())?.data;
        this.paginationConfig = {
            itemsPerPage: paginationCache?.itemsPerPage ?? Pagination.defaultItemsPerPage,
            totalItems: this.modelsCollection.length,
            pagesChunk: paginationCache?.pagesChunk ?? Pagination.defaultPagesChunk,
            configChanged: new EventEmitter(),
            currentPage: paginationCache?.currentPage ?? Pagination.defaultPage
        };

        const filterModelCache = this.cacheCollectionService.getItem(this.componentStorageKeyProvider.getFilterModelKey())?.data;
        if (filterModelCache) {
            this.filterModel.setPageNumber(filterModelCache.pageNumber);
            this.filterModel.setOrderColumn(filterModelCache.order_column);
            this.filterModel.setPageSize(filterModelCache.pageSize);
            this.filterModel.setOrderAscending(filterModelCache.order_ascending);
        }

        this.isFilterChanged = false;
        this.filterModelSubject = new Subject();

        this.filterSubjectSubscription = this.filterModelSubject.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            switchMap((filter: FilterModel) => this.getReportHandler(filter)),
        ).subscribe((response: ApiResponse<ApiError[] | ReportInterfaceType[]>) => {
            if (response.status !== HttpStatusCodesEnum.Ok) {
                this.router
                    .navigate(['/error'], {
                        state: {
                            statuses: [response.status],
                            messages: response.messages
                        }
                    })
                    .finally();

                return false;
            }

            const paginateTo = this.paginateToPage ?? Pagination.calculateItemsPerPage(this.filterModel.pageSize);
            const paginateFrom = this.paginateFromPage ?? 0;

            this.filterResultSuccessHandler(this.paginationConfig.currentPage, paginateFrom, paginateTo, response);
        });
    }

    forceFilterData(filter: FilterModel): Subscription {
        this.showLoader = true;
        return this.getReportHandler(filter)
            .subscribe((response: ApiResponse<ApiError[] | ReportInterfaceType[]>) => {
                this.showLoader = false;
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    this.router
                        .navigate(['/error'], {
                            state: {
                                statuses: [response.status],
                                messages: response.messages
                            }
                        })
                        .finally();

                    return false;
                }

                const paginateTo = Pagination.calculateItemsPerPage(this.filterModel.pageSize);

                this.filterResultSuccessHandler(1, 0, paginateTo, response);
            });
    }

    onFilterChanged(data: { [key: string]: string | number | boolean }): void {
        const filterModel = this.getFilterModel(data);
        this.reloadData(filterModel);
    }

    onClearTriggered(data: { [key: string]: string | number | boolean }): void {
        this.cacheCollectionService.removeItem(this.componentStorageKeyProvider.getFilterValueKey());
        this.cacheCollectionService.removeItem(this.componentStorageKeyProvider.getShownFilterKey());
        this.cacheCollectionService.removeItem(this.componentStorageKeyProvider.getHiddenFilterKey());
        this.cacheCollectionService.removeItem(this.componentStorageKeyProvider.getFilterModelKey());
        this.cacheCollectionService.removeItem(this.componentStorageKeyProvider.getPaginationConfigKey());
    }

    reloadData(filter: FilterModel): void {
        this.paginationConfig.currentPage = Pagination.initCurrentPage();

        this.isFilterChanged = true;
        this.filterModel = filter;

        this.paginateFromPage = 0;
        this.paginateToPage = Pagination.calculateItemsPerPage(this.filterModel.pageSize);

        this.filterModelSubject.next(this.filterModel);
    }

    fixPaginationAndDataSource(initialPaginationPage: number, paginateFrom: number, paginateTo: number): void {
        this.paginationConfig.pagesChunk = this.filterModel.pageNumber;
        this.paginationConfig.totalItems = this.modelsCollection.length;
        this.dataTableConfig.headerChecked = false;
        this.paginationConfig.itemsPerPage =
            Pagination.calculateItemsPerPage(this.filterModel.pageSize);

        this.paginationConfig.configChanged.emit({initialPage: initialPaginationPage});

        const paginatedCollection = this.paginateCollection<ReportModelType>(
            this.modelsCollection,
            paginateFrom,
            paginateTo
        );

        this.dataTableConfig.rows = this.createDataSource(paginatedCollection)
            .map((row: ReportTableDataRow) => {
                row.dataItems.map((item: ReportTableData) => {
                    if (this.dataTableConfig.hiddenColumnIds.indexOf(item.colId) >= 0) {
                        item.hidden = true;
                    }
                });

                return row;
            });
    }

    sortData(column: SortColumnInterface): void {
        this.sortedColumn = column;

        this.filterModel.setOrderColumn(this.sortedColumn.selector);
        this.filterModel.setOrderAscending(this.sortedColumn.ascending);

        this.forceFilterData(this.filterModel);
    }
}
